import { isPlatformBrowser } from '@angular/common';
import {
  ApplicationRef,
  ComponentRef,
  createComponent,
  DOCUMENT,
  EnvironmentInjector,
  inject,
  Injectable,
  Injector,
  OnDestroy,
  PLATFORM_ID,
  signal,
} from '@angular/core';

import { SnackbarStackComponent } from './snackbar-stack/snackbar-stack.component';
import {
  SNACKBAR_HOST,
  SNACKBAR_LEAVE_ANIMATION_MS,
  SnackbarHost,
  SnackbarItem,
  SnackbarType,
} from './snackbar.model';

const DEFAULT_DURATION = 5000;
const MAX_VISIBLE = 4;

const MERGE_DUPLICATES = false;

interface SnackbarTimer {
  handle: ReturnType<typeof setTimeout> | null;
  remaining: number;
  startedAt: number;
}

@Injectable({
  providedIn: 'root'
})
export class SnackbarService implements SnackbarHost, OnDestroy {
  private readonly _appRef = inject(ApplicationRef);
  private readonly _environmentInjector = inject(EnvironmentInjector);
  private readonly _document = inject(DOCUMENT);
  private readonly _isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private readonly _items = signal<readonly SnackbarItem[]>([]);
  private readonly _timers = new Map<number, SnackbarTimer>();
  private _stackRef: ComponentRef<SnackbarStackComponent> | null = null;
  private _nextId = 0;

  readonly items = this._items.asReadonly();

  ngOnDestroy(): void {
    this._timers.forEach(timer => {
      if (timer.handle) {
        clearTimeout(timer.handle);
      }
    });
    this._timers.clear();
    this._items.set([]);

    if (this._stackRef) {
      const host = this._stackRef.location.nativeElement as HTMLElement;
      if (!this._appRef.destroyed) {
        this._appRef.detachView(this._stackRef.hostView);
        this._stackRef.destroy();
      }
      host.remove();
      this._stackRef = null;
    }
  }

  openSnackBar(message: string, action: string = 'OK'): number {
    return this._open(message, action, 'default', DEFAULT_DURATION);
  }

  openSuccessSnackBar(message: string, action: string = 'OK'): number {
    return this._open(message, action, 'success', DEFAULT_DURATION);
  }

  openFailureSnackBar(message: string, action: string = 'OK'): number {
    return this._open(message, action, 'failure', DEFAULT_DURATION);
  }

  dismiss(id: number): void {
    const item = this._items().find(snackbar => snackbar.id === id);
    if (!item || item.leaving) {
      return;
    }

    this._clearTimer(id);
    this._patch(id, { leaving: true });

    setTimeout(
      () => this._items.update(items => items.filter(snackbar => snackbar.id !== id)),
      SNACKBAR_LEAVE_ANIMATION_MS
    );
  }

  dismissAll(): void {
    this._items().forEach(snackbar => this.dismiss(snackbar.id));
  }

  pauseTimer(id: number): void {
    const timer = this._timers.get(id);
    if (!timer?.handle) {
      return;
    }

    clearTimeout(timer.handle);
    timer.handle = null;
    timer.remaining = Math.max(0, timer.remaining - (Date.now() - timer.startedAt));
    this._patch(id, { paused: true });
  }

  resumeTimer(id: number): void {
    const timer = this._timers.get(id);
    if (!timer || timer.handle || timer.remaining <= 0) {
      return;
    }

    timer.startedAt = Date.now();
    timer.handle = setTimeout(() => this.dismiss(id), timer.remaining);
    this._patch(id, { paused: false });
  }

  private _open(message: string, action: string, type: SnackbarType, duration: number): number {
    if (!this._isBrowser) {
      return -1;
    }

    if (MERGE_DUPLICATES) {
      const duplicate = this._items().find(
        snackbar => !snackbar.leaving && snackbar.message === message && snackbar.type === type
      );

      if (duplicate) {
        this._clearTimer(duplicate.id);
        this._startTimer(duplicate.id, duration);
        this._patch(duplicate.id, { duration, paused: false, cycle: duplicate.cycle + 1 });
        return duplicate.id;
      }
    }

    const visible = this._items().filter(snackbar => !snackbar.leaving);
    if (visible.length >= MAX_VISIBLE) {
      this.dismiss(visible[0].id);
    }

    const id = this._nextId++;
    this._ensureStack();
    this._items.update(items => [
      ...items,
      { id, message, action, type, duration, paused: false, cycle: 0, leaving: false },
    ]);
    this._startTimer(id, duration);

    return id;
  }

  private _patch(id: number, changes: Partial<SnackbarItem>): void {
    this._items.update(items =>
      items.map(snackbar => snackbar.id === id ? { ...snackbar, ...changes } : snackbar)
    );
  }

  private _startTimer(id: number, duration: number): void {
    if (duration <= 0) {
      return;
    }

    this._timers.set(id, {
      handle: setTimeout(() => this.dismiss(id), duration),
      remaining: duration,
      startedAt: Date.now(),
    });
  }

  private _clearTimer(id: number): void {
    const timer = this._timers.get(id);
    if (timer?.handle) {
      clearTimeout(timer.handle);
    }
    this._timers.delete(id);
  }

  private _ensureStack(): void {
    if (this._stackRef) {
      return;
    }

    this._stackRef = createComponent(SnackbarStackComponent, {
      environmentInjector: this._environmentInjector,
      elementInjector: Injector.create({
        providers: [{ provide: SNACKBAR_HOST, useValue: this }],
        parent: this._environmentInjector,
      }),
    });

    this._appRef.attachView(this._stackRef.hostView);
    this._document.body.appendChild(this._stackRef.location.nativeElement);
  }
}
